import {
  publicUvToShaderUv
} from "./index-gnsce796.mjs";
import {
  assertOptionalFiniteNumber,
  validateNonNegative
} from "./index-wxhvgv2s.mjs";
import {
  WEBGPU_SINGLE_PASS_VERTEX,
  setupWebGpuSinglePass,
  cleanupWebGpuSinglePass,
  applyWebGpuSinglePass
} from "./index-tvfexb7s.mjs";
import {
  assertEffectParamsObject
} from "./index-4fvp93ps.mjs";

// src/fisheye/index.ts
import { Internals as Internals2 } from "remotion";

// src/fisheye/fisheye-runtime.ts
import { Internals } from "remotion";

// src/fisheye/fisheye-shaders.ts
var FISHEYE_VS = `#version 300 es
layout(location = 0) in vec2 aPos;
layout(location = 1) in vec2 aUv;
out vec2 vUv;
void main() {
	vUv = aUv;
	gl_Position = vec4(aPos, 0.0, 1.0);
}
`;
var FISHEYE_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 fragColor;

uniform sampler2D uSource;
uniform vec2 uCenter;
uniform float uFieldOfView;
uniform float uRadius;
uniform float uZoom;
uniform float uAspect;

void main() {
	vec2 toCenter = vUv - uCenter;
	vec2 aspectCorrected = vec2(toCenter.x * uAspect, toCenter.y);

	float dist = length(aspectCorrected);
	float r = dist / max(uRadius, 0.0001);

	if (r > 1.0 || uFieldOfView <= 0.0001) {
		fragColor = texture(uSource, vUv);
		return;
	}

	float halfFov = uFieldOfView * 0.5;
	float warped = tan(r * halfFov) / tan(halfFov);

	float scale = (dist > 0.00001) ? (warped * uRadius / dist) : 0.0;
	vec2 warpedAspect = aspectCorrected * scale;
	vec2 warpedOffset = vec2(warpedAspect.x / uAspect, warpedAspect.y);
	warpedOffset /= max(uZoom, 0.0001);

	vec2 srcUv = uCenter + warpedOffset;

	if (
		srcUv.x < 0.0 ||
		srcUv.x > 1.0 ||
		srcUv.y < 0.0 ||
		srcUv.y > 1.0
	) {
		fragColor = vec4(0.0);
		return;
	}

	fragColor = texture(uSource, srcUv);
}
`;

// src/fisheye/fisheye-runtime.ts
var { createWebGL2ContextError } = Internals;
var compileShader = (gl, type, source) => {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create WebGL shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile failed: ${log ?? "(no log)"}`);
  }
  return shader;
};
var linkProgram = (gl, vs, fs) => {
  const program = gl.createProgram();
  if (!program) {
    throw new Error("Failed to create WebGL program");
  }
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link failed: ${log ?? "(no log)"}`);
  }
  return program;
};
var createProgram = (gl, vertexSource, fragmentSource) => {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = linkProgram(gl, vs, fs);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
};
var createRgbaTexture = (gl) => {
  const texture = gl.createTexture();
  if (!texture) {
    throw new Error("Failed to create WebGL texture");
  }
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
};
var setupFisheye = (target) => {
  const gl = target.getContext("webgl2", {
    premultipliedAlpha: true,
    alpha: true,
    preserveDrawingBuffer: true
  });
  if (!gl) {
    throw createWebGL2ContextError("fisheye effect");
  }
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  const program = createProgram(gl, FISHEYE_VS, FISHEYE_FS);
  const vao = gl.createVertexArray();
  if (!vao) {
    throw new Error("Failed to create WebGL vertex array");
  }
  gl.bindVertexArray(vao);
  const data = new Float32Array([
    -1,
    -1,
    0,
    0,
    1,
    -1,
    1,
    0,
    -1,
    1,
    0,
    1,
    1,
    1,
    1,
    1
  ]);
  const vbo = gl.createBuffer();
  if (!vbo) {
    throw new Error("Failed to create WebGL buffer");
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 8);
  gl.bindVertexArray(null);
  const textureSource = createRgbaTexture(gl);
  return {
    gl,
    program,
    vao,
    vbo,
    textureSource,
    uniforms: {
      uSource: gl.getUniformLocation(program, "uSource"),
      uCenter: gl.getUniformLocation(program, "uCenter"),
      uFieldOfView: gl.getUniformLocation(program, "uFieldOfView"),
      uRadius: gl.getUniformLocation(program, "uRadius"),
      uZoom: gl.getUniformLocation(program, "uZoom"),
      uAspect: gl.getUniformLocation(program, "uAspect")
    }
  };
};
var cleanupFisheye = (state) => {
  const { gl, program, vao, vbo, textureSource } = state;
  gl.deleteTexture(textureSource);
  gl.deleteBuffer(vbo);
  gl.deleteProgram(program);
  gl.deleteVertexArray(vao);
};
var drawFullscreenQuad = (state) => {
  const { gl, vao } = state;
  gl.bindVertexArray(vao);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  gl.bindVertexArray(null);
};
var applyFisheye = ({
  state,
  source,
  width,
  height,
  center,
  fieldOfView,
  radius,
  zoom,
  flipSourceY
}) => {
  const { gl, program, textureSource, uniforms } = state;
  gl.viewport(0, 0, width, height);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, flipSourceY);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(program);
  if (uniforms.uSource)
    gl.uniform1i(uniforms.uSource, 0);
  if (uniforms.uCenter) {
    const shaderCenter = publicUvToShaderUv(center);
    gl.uniform2f(uniforms.uCenter, shaderCenter[0], shaderCenter[1]);
  }
  if (uniforms.uFieldOfView)
    gl.uniform1f(uniforms.uFieldOfView, fieldOfView);
  if (uniforms.uRadius)
    gl.uniform1f(uniforms.uRadius, radius);
  if (uniforms.uZoom)
    gl.uniform1f(uniforms.uZoom, zoom);
  if (uniforms.uAspect)
    gl.uniform1f(uniforms.uAspect, width / height);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, textureSource);
  drawFullscreenQuad(state);
  gl.bindTexture(gl.TEXTURE_2D, null);
  gl.useProgram(null);
};

// src/fisheye/webgpu-shader.ts
var FISHEYE_WEBGPU_SHADER = `
${WEBGPU_SINGLE_PASS_VERTEX}
@group(0) @binding(0) var source: texture_2d<f32>;
@group(0) @binding(1) var sourceSampler: sampler;
@group(0) @binding(2) var<uniform> params: Params;
struct Params { centerAndLens: vec4f, zoomAndAspect: vec4f, }
@fragment fn fragment(input: VertexOutput) -> @location(0) vec4f {
  let center = params.centerAndLens.xy;
  let fieldOfView = params.centerAndLens.z;
  let radius = params.centerAndLens.w;
  let zoom = params.zoomAndAspect.x;
  let aspect = params.zoomAndAspect.y;
  let toCenter = input.uv - center;
  let corrected = vec2f(toCenter.x * aspect, toCenter.y);
  let dist = length(corrected);
  let r = dist / max(radius, 0.0001);
  if (r > 1.0 || fieldOfView <= 0.0001) {
    return textureSampleLevel(source, sourceSampler, input.uv, 0);
  }
  let halfFov = fieldOfView * 0.5;
  let warped = tan(r * halfFov) / tan(halfFov);
  var scale = 0.0;
  if (dist > 0.00001) { scale = warped * radius / dist; }
  let warpedAspect = corrected * scale;
  let offset = vec2f(warpedAspect.x / aspect, warpedAspect.y) / max(zoom, 0.0001);
  let uv = center + offset;
  if (any(uv < vec2f(0)) || any(uv > vec2f(1))) { return vec4f(0); }
  return textureSampleLevel(source, sourceSampler, uv, 0);
}
`;

// src/fisheye/index.ts
var { createEffect } = Internals2;
var DEFAULT_FISHEYE_FIELD_OF_VIEW = 2.5;
var DEFAULT_FISHEYE_CENTER = [0.5, 0.5];
var DEFAULT_FISHEYE_RADIUS = 1;
var DEFAULT_FISHEYE_ZOOM = 1;
var MAX_FIELD_OF_VIEW = Math.PI;
var fisheyeSchema = {
  fieldOfView: {
    type: "number",
    min: 0,
    max: MAX_FIELD_OF_VIEW,
    step: 0.01,
    default: DEFAULT_FISHEYE_FIELD_OF_VIEW,
    description: "Field of view",
    hiddenFromList: false
  },
  center: {
    type: "uv-coordinate",
    min: 0,
    max: 1,
    step: 0.01,
    default: DEFAULT_FISHEYE_CENTER,
    description: "Center"
  },
  radius: {
    type: "number",
    min: 0.01,
    max: 3,
    step: 0.01,
    default: DEFAULT_FISHEYE_RADIUS,
    description: "Radius",
    hiddenFromList: false
  },
  zoom: {
    type: "number",
    min: 0.1,
    max: 5,
    step: 0.01,
    default: DEFAULT_FISHEYE_ZOOM,
    description: "Zoom",
    hiddenFromList: false
  }
};
var resolve = (p) => ({
  fieldOfView: p.fieldOfView ?? DEFAULT_FISHEYE_FIELD_OF_VIEW,
  center: [...p.center ?? DEFAULT_FISHEYE_CENTER],
  radius: p.radius ?? DEFAULT_FISHEYE_RADIUS,
  zoom: p.zoom ?? DEFAULT_FISHEYE_ZOOM
});
var assertOptionalUvCoordinate = (value, name) => {
  if (value === undefined) {
    return;
  }
  if (!Array.isArray(value) || value.length !== 2 || value.some((item) => typeof item !== "number" || !Number.isFinite(item))) {
    throw new TypeError(`"${name}" must be a [number, number] tuple`);
  }
};
var validatePositive = (value, name) => {
  if (value <= 0) {
    throw new TypeError(`"${name}" must be greater than 0, but got ${JSON.stringify(value)}`);
  }
};
var validateFisheyeParams = (params) => {
  assertEffectParamsObject(params, "Fisheye");
  assertOptionalFiniteNumber(params.fieldOfView, "fieldOfView");
  assertOptionalUvCoordinate(params.center, "center");
  assertOptionalFiniteNumber(params.radius, "radius");
  assertOptionalFiniteNumber(params.zoom, "zoom");
  const resolved = resolve(params);
  validateNonNegative(resolved.fieldOfView, "fieldOfView");
  if (resolved.fieldOfView > MAX_FIELD_OF_VIEW) {
    throw new TypeError(`"fieldOfView" must be <= ${MAX_FIELD_OF_VIEW}, but got ${JSON.stringify(resolved.fieldOfView)}`);
  }
  validatePositive(resolved.radius, "radius");
  validatePositive(resolved.zoom, "zoom");
};
var fisheye = createEffect({
  type: "dev.remotion.effects.fisheye",
  label: "fisheye()",
  documentationLink: "https://www.remotion.dev/docs/effects/fisheye",
  calculateKey: (params) => {
    const r = resolve(params);
    return `fisheye-${r.fieldOfView}-${r.center.join(":")}-${r.radius}-${r.zoom}`;
  },
  implementations: {
    webgpu: Internals2.createEffectImplementation({
      setup: (target) => setupWebGpuSinglePass(target, "fisheye"),
      apply: ({ source, width, height, params, state, gpuDevice }) => {
        if (!gpuDevice) {
          throw new Error("A WebGPU device is required for the fisheye effect");
        }
        const r = resolve(params);
        const uniformData = new Float32Array([
          r.center[0],
          r.center[1],
          r.fieldOfView,
          r.radius,
          r.zoom,
          width / height,
          0,
          0
        ]);
        applyWebGpuSinglePass({
          state,
          source,
          width,
          height,
          device: gpuDevice,
          shader: FISHEYE_WEBGPU_SHADER,
          uniformData
        });
      },
      cleanup: cleanupWebGpuSinglePass
    }),
    webgl2: Internals2.createEffectImplementation({
      setup: (target) => setupFisheye(target),
      apply: ({ source, width, height, params, state, flipSourceY }) => {
        const r = resolve(params);
        applyFisheye({
          state,
          source,
          width,
          height,
          center: r.center,
          fieldOfView: r.fieldOfView,
          radius: r.radius,
          zoom: r.zoom,
          flipSourceY
        });
      },
      cleanup: (state) => cleanupFisheye(state)
    })
  },
  schema: fisheyeSchema,
  validateParams: validateFisheyeParams
});
export {
  fisheye
};
